/**
 * Edit Sent – senders half + AI senders (extracted for file size).
 */

import { AnimatePresence } from 'framer-motion';

import { AiGenerateSendersDropdown } from '@/features/campaign-texts/components/AiGenerateSendersDropdown';
import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import { GeneratedSendersPreview } from '@/features/campaign-texts/components/GeneratedSendersPreview';
import { GeneratedSendersPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedSendersPreviewPerDrop';
import { SendersPickerSection } from '@/features/smswiser-campaigns/components/drop-modals';
import type { ScaleDropModalProps } from '@/features/smswiser-campaigns/components/ScaleDropModal/ScaleDropModal.types';

import { useAiSenderGeneration } from '@/features/smswiser-campaigns/components/ScaleDropModal/modals/scale-or-edit/creative/useAiSenderGeneration';
import type { useEditSentDropForm } from '../useEditSentDropForm';

type AiSendersApi = ReturnType<typeof useAiSenderGeneration>;

interface Props {
  form: ReturnType<typeof useEditSentDropForm>;
  isBusy: boolean;
  countryKey: string;
  dropCount: number;
  dropLabels: DropLabel[];
  showAiSenders: boolean;
  onAddSender?: ScaleDropModalProps['onAddSender'];
  aiSenders: AiSendersApi;
}

export function EditSentDropCreativeSendersBlock(props: Props) {
  const { form, isBusy, countryKey, dropCount, dropLabels, showAiSenders, onAddSender, aiSenders } = props;
  return (
    <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
      <SendersPickerSection
        sendersList={form.sendersList}
        selectedSenders={form.selectedSenderNames}
        onToggleSender={form.toggleSender}
        onSelectAll={form.selectAllSenders}
        onClearSelection={form.clearSendersSelection}
        search={form.senderSearch}
        onSearch={form.setSenderSearch}
        newValue={form.newSenderValue}
        setNewValue={form.setNewSenderValue}
        onAdd={onAddSender ? form.handleAddSender : undefined}
        isBusy={isBusy}
        expanded={form.showSenders}
        onToggleExpand={() => form.setShowSenders((v) => !v)}
        templates={form.senderTemplates}
        activeTemplateId={form.activeTemplateId}
        onSelectTemplate={form.handleSelectTemplate}
        onCreateTemplate={form.handleSaveAsTemplate}
        canSaveTemplate={form.effectiveRouteIdForTemplates > 0}
        pendingHideSenders={form.pendingHideSenders}
        replacedByAiSenders={form.replacedByAiSenders}
        actionSlot={
          showAiSenders ? (
            <AiGenerateSendersDropdown
              countryKey={countryKey}
              dropCount={dropCount}
              dropLabels={dropLabels}
              onGenerated={aiSenders.handleAiGenerated}
            />
          ) : undefined
        }
        toolbarSlot={
          showAiSenders ? (
            <AnimatePresence>
              {aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0 ? (
                <GeneratedSendersPreviewPerDrop
                  batches={aiSenders.perDropBatches}
                  groupIdsPerDrop={aiSenders.groupIdsPerDrop}
                  dropLabels={dropLabels}
                  onSave={aiSenders.handleSavePerDrop}
                  onDismiss={aiSenders.handleDismissPerDrop}
                  onRegenerate={aiSenders.handleRegeneratePerDrop}
                  onRegenerateAll={aiSenders.handleRegenerateAllPerDrop}
                  onSaveAndHide={aiSenders.handleSaveAndHidePerDrop}
                  isRegenerating={aiSenders.isRegenerating}
                  useAndHideOnly={dropCount > 1}
                  applied={aiSenders.perDropApplied}
                  generationTimeMs={aiSenders.perDropGenerationTimeMs}
                  fillHeight
                />
              ) : aiSenders.generatedSenders.length > 0 ? (
                <GeneratedSendersPreview
                  senders={aiSenders.generatedSenders}
                  onSave={aiSenders.handleSaveGenerated}
                  onSaveAndHide={aiSenders.handleSaveAndHide}
                  onDismiss={aiSenders.handleDismiss}
                  onRegenerate={aiSenders.handleRegenerate}
                  isRegenerating={aiSenders.isRegenerating}
                  fillHeight
                />
              ) : null}
            </AnimatePresence>
          ) : undefined
        }
      />
    </div>
  );
}
